import {
  ComparisonSpecType,
  DateGranularity,
  defaultComparisonForGranularity,
  isStaticComparison,
  NullableStatus,
} from '@tableau/metrics-engine';
import * as React from 'react';
import { exhaustivelyMatch } from '../../../utils';
import { StatusComponent } from '../status/StatusComponent';
import { ComparisonType, ComparisonTypeDropdownComponent } from './ComparisonTypeDropdown';
import { ConstantComparisonValueInput } from './ConstantComparisonValueInput';
import { HistoricalComparisonComponent } from './HistoricalComparisonComponent';

export interface ComparisonComponentProps {
  // Feature Flags
  isReferenceLineEnabled?: boolean;
  isHistoricalComparisonsEnabled?: boolean;

  // Metric Context
  canAuthorHistoricalComparison?: boolean;
  dateGranularity: DateGranularity;
  formatString?: string;
  disabled?: boolean;
  validationCounter: number;

  readonly selectedComparison: ComparisonSpecType | null;
  readonly onComparisonChange: (comparison: ComparisonSpecType | null) => void;

  readonly status: NullableStatus;
  readonly onStatusChange: (status: NullableStatus) => void;

  dropdownRef?: React.RefObject<HTMLButtonElement>;
}

export const ComparisonComponent = (props: ComparisonComponentProps): JSX.Element => {
  const defaultComparisonType =
    props.selectedComparison !== null && isStaticComparison(props.selectedComparison)
      ? ComparisonType.CONSTANT
      : ComparisonType.HISTORICAL;

  const [selectedComparisonType, setSelectedComparisonType] = React.useState<ComparisonType>(defaultComparisonType);

  // This useEffect is written to catch the case when the ComparisonComponent re-renders with a
  // different props.selectedComparison, which may happen when the user resizes the window.
  // When the prop changes, we DON'T want to use the previous state value, we want to reset
  // the state value.
  React.useEffect(() => {
    const selectedComparisonTypeString = props.selectedComparison?.type;
    if (selectedComparisonTypeString === 'STATIC_COMPARISON') {
      setSelectedComparisonType(ComparisonType.CONSTANT);
    } else if (
      selectedComparisonTypeString === 'HISTORICAL_COMPARISON' ||
      selectedComparisonTypeString === 'MARK_BASED_COMPARISON'
    ) {
      setSelectedComparisonType(ComparisonType.HISTORICAL);
    }
  }, [props.selectedComparison]);

  const onComparisonTypeChange = (type: ComparisonType) => {
    setSelectedComparisonType(type);
    switch (type) {
      case ComparisonType.CONSTANT:
        props.onComparisonChange(null);
        break;
      case ComparisonType.HISTORICAL:
        props.onComparisonChange(defaultComparisonForGranularity(props.dateGranularity));
        props.onStatusChange({ above: null, equal: null, below: null });
        break;
      default:
        exhaustivelyMatch(type);
    }
  };
  const showComparisonDropdown = props.isReferenceLineEnabled;
  const showHistoricalComparison =
    props.isReferenceLineEnabled &&
    props.isHistoricalComparisonsEnabled &&
    props.canAuthorHistoricalComparison &&
    selectedComparisonType === ComparisonType.HISTORICAL;
  const showConstantComparison = props.isReferenceLineEnabled && selectedComparisonType === ComparisonType.CONSTANT;
  return (
    <>
      {showComparisonDropdown && (
        <ComparisonTypeDropdownComponent
          testId={'comparison-type-dropdown'}
          canAuthorHistoricalComparison={props.canAuthorHistoricalComparison}
          selectedComparisonType={selectedComparisonType}
          onComparisonChange={onComparisonTypeChange}
          disabled={props.disabled}
          dropdownRef={props.dropdownRef}
        />
      )}
      {showHistoricalComparison && (
        <HistoricalComparisonComponent
          granularity={props.dateGranularity}
          validationCounter={props.validationCounter}
          onSelectedComparisonChange={props.onComparisonChange}
          selectedComparison={props.selectedComparison}
          disabled={props.disabled}
        />
      )}
      {showConstantComparison && (
        <>
          <ConstantComparisonValueInput
            formatString={props.formatString}
            validationCounter={props.validationCounter}
            onSelectedComparisonChange={props.onComparisonChange}
            selectedComparison={props.selectedComparison}
            disabled={props.disabled}
          />
          <StatusComponent onStatusChange={props.onStatusChange} status={props.status} disabled={props.disabled} />
        </>
      )}
    </>
  );
};
